import { useState, useEffect } from 'react'
import { useAuth } from '../hooks/useAuth'
import Button from '../components/common/Button'
import Input from '../components/common/Input'
import TextArea from '../components/common/TextArea'
import toast from 'react-hot-toast'
import { useNavigate } from 'react-router-dom'
import { companyApi } from '../services/api'



export default function CompanySettings() {
    const { user } = useAuth()
    const navigate = useNavigate()
    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [form, setForm] = useState({
        companyName: '',
        addressLine1: '',
        addressLine2: '',
        phone: '',
        fax: '',
        website: '',
        rightLogoText: '',
        shopJobsite: ''
    })
    const [hasLeftLogo, setHasLeftLogo] = useState(false)
    const [hasRightLogo, setHasRightLogo] = useState(false)

    useEffect(() => {
        loadSettings()
    }, [])

    async function loadSettings() {
        try {
            const data = await companyApi.getSettings()
            if (data.success) {
                setForm({
                    companyName: data.settings.companyName || '',
                    addressLine1: data.settings.addressLine1 || '',
                    addressLine2: data.settings.addressLine2 || '',
                    phone: data.settings.phone || '',
                    fax: data.settings.fax || '',
                    website: data.settings.website || '',
                    rightLogoText: data.settings.rightLogoText || '',
                    shopJobsite: data.settings.shopJobsite || ''
                })
                setHasLeftLogo(data.settings.hasLeftLogo)
                setHasRightLogo(data.settings.hasRightLogo)
            }
        } catch (error) {
            toast.error('Error loading settings')
        }
        setLoading(false)
    }

    async function handleSave() {
        setSaving(true)
        try {
            const data = await companyApi.updateSettings(form)
            if (data.success) {
                toast.success('Settings saved')
                navigate('/sync')
            } else {
                toast.error(data.message || 'Failed to save')
            }
        } catch (error) {
            toast.error('Error saving settings')
        }
        setSaving(false)
    }

    async function handleLogoUpload(side, file) {
        if (!file) return
        try {
            const data = await companyApi.uploadLogo(side, file)
            if (data.success) {
                toast.success(`${side === 'left' ? 'Left' : 'Right'} logo uploaded`)
                if (side === 'left') setHasLeftLogo(true)
                else setHasRightLogo(true)
            } else {
                toast.error(data.message || 'Upload failed')
            }
        } catch (error) {
            toast.error('Error uploading logo')
        }
    }

    function handleChange(field, value) {
        setForm(prev => ({ ...prev, [field]: value }))
    }

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="spinner w-8 h-8"></div>
            </div>
        )
    }

    return (
        <div>
            <div className="page-header flex items-center justify-between">
                <h1 className="page-title">Company Information</h1>
                <Button variant="secondary" onClick={() => navigate('/sync')}>
                    ← Back to Sync Dashboard
                </Button>
            </div>

            <div className="bg-white rounded-xl shadow-md p-6">
                {/* Company Details */}
                <h2 className="text-lg font-semibold mb-4">Company Details</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6">
                    <Input
                        label="Company Name"
                        value={form.companyName}
                        onChange={(e) => handleChange('companyName', e.target.value)}
                    />
                    <Input
                        label="Address Line 1"
                        value={form.addressLine1}
                        onChange={(e) => handleChange('addressLine1', e.target.value)}
                        placeholder="e.g. 70 Ellis Dr Unit 7"
                    />
                    <Input
                        label="Address Line 2"
                        value={form.addressLine2}
                        onChange={(e) => handleChange('addressLine2', e.target.value)}
                        placeholder="e.g. Barrie, Ontario L4N 8Z3"
                    />
                    <Input
                        label="Phone"
                        value={form.phone}
                        onChange={(e) => handleChange('phone', e.target.value)}
                    />
                    <Input
                        label="Fax"
                        value={form.fax}
                        onChange={(e) => handleChange('fax', e.target.value)}
                    />
                    <Input
                        label="Website"
                        value={form.website}
                        onChange={(e) => handleChange('website', e.target.value)}
                    />
                </div>

                {/* Shop Job Site */}
                <h2 className="text-lg font-semibold mt-8 mb-4">PDF Settings</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6">
                    <Input
                        label="Repair In Shop / Internal Job Site"
                        value={form.shopJobsite}
                        onChange={(e) => handleChange('shopJobsite', e.target.value)}
                        placeholder="e.g. 70 Ellis Dr Unit 7, Barrie ON"
                    />
                </div>

                {/* Logos */}
                <h2 className="text-lg font-semibold mt-8 mb-4">PDF Logos</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Left Logo */}
                    <div className="border rounded-lg p-4">
                        <label className="form-label mb-2 block">Left Logo (Main Company Logo)</label>
                        {hasLeftLogo && (
                            <div className="mb-3 bg-gray-50 p-3 rounded">
                                <img
                                    src={`/api/company-settings/logo/left?t=${Date.now()}`}
                                    alt="Left Logo"
                                    className="max-h-20"
                                    onError={(e) => e.target.style.display = 'none'}
                                />
                            </div>
                        )}
                        <input
                            type="file"
                            accept=".png,.jpg,.jpeg"
                            onChange={(e) => handleLogoUpload('left', e.target.files[0])}
                            className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                        />
                        <p className="text-xs text-gray-400 mt-1">PNG or JPG, recommended ~300px wide</p>
                    </div>

                    {/* Right Logo */}
                    <div className="border rounded-lg p-4">
                        <label className="form-label mb-2 block">Right Logo (Optional Badge/Certification)</label>
                        {hasRightLogo && (
                            <div className="mb-3 bg-gray-50 p-3 rounded">
                                <img
                                    src={`/api/company-settings/logo/right?t=${Date.now()}`}
                                    alt="Right Logo"
                                    className="max-h-20"
                                    onError={(e) => e.target.style.display = 'none'}
                                />
                            </div>
                        )}
                        <input
                            type="file"
                            accept=".png,.jpg,.jpeg"
                            onChange={(e) => handleLogoUpload('right', e.target.files[0])}
                            className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                        />
                        <p className="text-xs text-gray-400 mt-1">PNG or JPG, recommended ~150px wide</p>
                    </div>
                </div>

                {/* Right Logo Text */}
                <div className="mt-4">
                    <TextArea
                        label="Text Under Right Logo (optional, e.g. Licence No.)"
                        value={form.rightLogoText}
                        onChange={(e) => handleChange('rightLogoText', e.target.value)}
                        rows={2}
                        maxLength={500}
                    />
                </div>

                {/* Save */}
                <div className="flex gap-3 mt-6 pt-4 border-t">
                    <Button onClick={handleSave} loading={saving}>
                        Save Settings
                    </Button>
                </div>
            </div>
        </div>
    )
}
